import { useCallback, useEffect, useState } from 'react';
import GradebookTable from 'src/Components/Admin/GradebookTable';
import { useParams } from 'react-router-dom';
import { getGroup, getSessionWithSessionId, getUser, downloadGradebookToCSV } from 'src/Services/BorgAPIClient';
import { Group, GroupCompetition, UserMinimal } from 'src/Constants/Types';
import { AlertColor, Box, Button, Typography } from '@mui/material';
import { colorAuriaYellow } from 'src/GlobalStyles/MuiTheme';
import DownloadIcon from '@mui/icons-material/Download';
import { getCourseId } from 'src/Constants/ScenarioData';
import { removeIllegalChars } from 'src/Util/Util';

const GradebookPage = ({ onNotification }: {
  onNotification: (message: string, notificationType?: AlertColor) => void
}) => {

  const { sessionId } = useParams<{ sessionId: string }>();

  const [sessionData, setSessionData] = useState<GroupCompetition | undefined>();
  const [groupData, setGroupData] = useState<Group | undefined>();
  const [sessionStudents, setSessionStudents] = useState<UserMinimal[] | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const fetchSessionAndGroup = useCallback(async () => {
    if (!sessionId) return;

    try {
      //Obtain Session Information
      const sessionResponse = await getSessionWithSessionId(sessionId);
      setSessionData(sessionResponse.data as GroupCompetition);

      if (sessionResponse.data && sessionResponse.data.groupId) {
        //Obtain Group Information
        const groupResponse = await getGroup(sessionResponse.data.groupId);
        setGroupData(groupResponse.data as Group);
      }
    } catch (error) {
      let msg = "Failed to fetch course or session data";
      console.error(msg, error);
      onNotification(msg, "error");
    }
  }, [sessionId]);

  const createSessionStudentsList = async () => {
    if (groupData) {
      let studentId;
      let tempSessionStudents: UserMinimal[] = [];

      //Step through members list and get list of students in the group and their names
      for (let i = 0; i < groupData.memberIds.length; i++) {
        studentId = groupData.memberIds[i];

        let response;
        try {
          response = await getUser(studentId.replace(/"|'/g, ''));
        } catch (err) {
          let msg = "Failed to fetch user data";
          console.error(msg, err);
          onNotification(msg, "error");
        }

        if (response?.data) {
          tempSessionStudents[i] = {
            id: groupData.memberIds[i],
            username: response.data.username,
          };
        }
      }

      // Sort the students by their username 
      tempSessionStudents.sort((a, b) => a.username.localeCompare(b.username));

      setSessionStudents(tempSessionStudents);
    }

    if (groupData) {
      setIsLoaded(true);
    }
  };

  const handleDownloadGradebookClick = async () => {
    if (!sessionData || !groupData?.ownerOrganizationId) {
      return;
    }

    const courseId = getCourseId(groupData.ownerOrganizationId);

    let filename = `Gradebook - ${sessionData.name}.csv`;
    filename = removeIllegalChars(filename);

    try {
      await downloadGradebookToCSV(sessionData.id, courseId, filename);
    } catch (error) {
      const msg = "Failed to download gradebook";
      console.error(msg, error);
      onNotification(msg, "error");
    }
  }

  const renderGradebookTable = () => {
    if (sessionStudents && sessionStudents.length > 0) {
      return (<GradebookTable sessionStudents={sessionStudents} session={sessionData} onNotification={onNotification} />)
    }
    return <Typography align='center' variant='h2' sx={{ margin: 10 }}>No Students Enrolled</Typography>
  }

  useEffect(() => {
    fetchSessionAndGroup();
  }, [fetchSessionAndGroup]);

  useEffect(() => {
    createSessionStudentsList();
  }, [groupData]);

  if (isLoaded) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {/* Header Section - does NOT scroll */}
        <Box sx={{ flexShrink: 0 }}>
          <Box sx={{ margin: 'auto', alignContent: 'left' }}>
            <h1>Gradebook for {groupData?.name}</h1>
            <p>Session Name: {sessionData?.name}</p>

            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadGradebookClick}
              sx={{ mt: 1, mb: 1 }}
              startIcon={<DownloadIcon />}
            >
              Download CSV
            </Button>

            {!groupData?.activeSessionId && (
              <Typography
                variant="body2"
                sx={{
                  fontStyle: 'italic',
                  my: 1,
                  color: colorAuriaYellow,
                }}
              >
                This course is not currently active...
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          {renderGradebookTable()}
        </Box>
      </Box>
    );
  } else {
    return <div>Loading...</div>
  }
};

export default GradebookPage;